const CITY_TYPE_PRIORITY = [
  "locality", // 도시 또는 마을
  "postal_town", // 우편 도시 (일부 국가에서 사용)
  "sublocality", // 하위 지역 (숫자가 클수록 더 작은 지역)
  "sublocality_level_1",
  "sublocality_level_2",
  "sublocality_level_3",
  "sublocality_level_4",
  "sublocality_level_5",
  "administrative_area_level_3", // 하위 행정 구역 (숫자가 클수록 더 낮은 행정 구역)
  "administrative_area_level_4",
  "administrative_area_level_5",
  "administrative_area_level_6",
  "administrative_area_level_7",
  "neighborhood", // 동네
] as const;

const PROVINCE_TYPE_PRIORITY = [
  "administrative_area_level_1", // 주/도
  "administrative_area_level_2", // 시/군/구
] as const;

const CITY_FALLBACK_TYPES = ["administrative_area_level_2", "administrative_area_level_4", "administrative_area_level_5"] as const;

/**
 * stateOptions에서 매칭되는 province 찾기
 */
export const findMatchingProvince = (
  component: google.maps.places.AddressComponent,
  _stateOptions: { value: string; name: string }[],
): { code: string; name: string } | null => {
  // shortText (코드)로 먼저 찾기
  const byCode = _stateOptions.find((opt) => opt.value === component.shortText);
  if (byCode) {
    return { code: byCode.value, name: byCode.name };
  }

  // longText (이름)로 찾기
  const byName = _stateOptions.find((opt) => opt.name === component.longText);
  if (byName) {
    return { code: byName.value, name: byName.name };
  }

  return null;
};

/**
 * Google Place의 addressComponents를 폼 데이터로 파싱
 */
export const parseGooglePlaceAddress = (
  addressComponents: google.maps.places.AddressComponent[],
  _stateOptions: { value: string; name: string }[],
): {
  address1: string;
  city: string;
  provinceCode: string;
  provinceName: string;
  zipCode: string;
} => {
  const result: {
    address1: string;
    city: string;
    provinceCode: string;
    provinceName: string;
    zipCode: string;
  } = {
    address1: "",
    city: "",
    provinceCode: "",
    provinceName: "",
    zipCode: "",
  };

  // 1. City 파싱 (우선순위 순서대로)
  for (const priorityType of CITY_TYPE_PRIORITY) {
    const cityComponent = addressComponents.find((item) => item.types.includes(priorityType));
    if (cityComponent?.longText) {
      result.city = cityComponent.longText;
      break;
    }
  }

  // 2. Province 파싱 (우선순위 순서대로)
  for (const priorityType of PROVINCE_TYPE_PRIORITY) {
    const provinceComponent = addressComponents.find((item) => item.types.includes(priorityType));
    if (provinceComponent) {
      const matched = findMatchingProvince(provinceComponent, _stateOptions);
      if (matched) {
        result.provinceCode = matched.code;
        result.provinceName = matched.name;
        break;
      }
    }
  }

  // 3. City가 없을 경우 fallback 처리
  if (!result.city) {
    for (const fallbackType of CITY_FALLBACK_TYPES) {
      const fallbackComponent = addressComponents.find((item) => item.types.includes(fallbackType));
      if (fallbackComponent?.longText) {
        result.city = fallbackComponent.longText;
        break;
      }
    }
  }

  // 4. ProvinceCode가 없을 경우 fallback 처리
  if (!result.provinceCode) {
    const adminAreaLevel1 = addressComponents.find((item) => item.types.includes("administrative_area_level_1"));
    if (adminAreaLevel1) {
      const matched = findMatchingProvince(adminAreaLevel1, _stateOptions);
      if (matched) {
        result.provinceCode = matched.code;
        result.provinceName = matched.name;
      }
    }
  }

  // 4. ProvinceName만 있고 provinceCode가 없는 경우 재매칭
  if (!result.provinceCode && result.provinceName) {
    const matchedOption = _stateOptions.find((option) => option.name === result.provinceName);
    if (matchedOption) {
      result.provinceCode = matchedOption.value;
    }
  }

  // 5. Zip Code 파싱
  const zipComponent = addressComponents.find((item) => item.types.includes("postal_code"));
  result.zipCode = zipComponent?.longText || "";

  return result;
};
